import { create } from 'zustand';
import { api } from '../services/api';
import type { Message, ConversationSummary } from '../types/messaging';

interface MessageState {
  conversations: ConversationSummary[];
  activeConversationId: string | null;
  messages: Record<string, Message[]>;
  unreadCount: number;
  isLoadingConversations: boolean;
  isLoadingMessages: boolean;
  error: string | null;

  fetchConversations: () => Promise<void>;
  fetchMessages: (conversationId: string) => Promise<void>;
  sendMessage: (recipientId: string, content: string) => Promise<void>;
  markAsRead: (conversationId: string) => Promise<void>;
  setActiveConversation: (conversationId: string | null) => void;
  refreshUnreadCount: () => Promise<void>;
}

export const useMessageStore = create<MessageState>((set, get) => ({
  conversations: [],
  activeConversationId: null,
  messages: {},
  unreadCount: 0,
  isLoadingConversations: false,
  isLoadingMessages: false,
  error: null,

  fetchConversations: async () => {
    set({ isLoadingConversations: true, error: null });
    try {
      const conversations = await api.messages.getConversations();
      set({ conversations, isLoadingConversations: false });
    } catch (error: any) {
      set({ error: error.message || 'Failed to load conversations', isLoadingConversations: false });
    }
  },

  fetchMessages: async (conversationId: string) => {
    set({ isLoadingMessages: true, error: null });
    try {
      const msgs = await api.messages.getMessages(conversationId);
      set(state => ({
        messages: { ...state.messages, [conversationId]: msgs },
        isLoadingMessages: false
      }));
    } catch (error: any) {
      set({ error: error.message || 'Failed to load messages', isLoadingMessages: false });
    }
  },

  sendMessage: async (recipientId: string, content: string) => {
    try {
      const msg = await api.messages.sendMessage(recipientId, content);
      set(state => {
        const existing = state.messages[msg.conversationId] || [];
        return {
          messages: {
            ...state.messages,
            [msg.conversationId]: [...existing, msg]
          }
        };
      });
      // Refresh conversation list to update last message
      get().fetchConversations();
    } catch (error: any) {
      set({ error: error.message || 'Failed to send message' });
      throw error;
    }
  },

  markAsRead: async (conversationId: string) => {
    try {
      await api.messages.markAsRead(conversationId);
      set(state => ({
        conversations: state.conversations.map(c =>
          c.conversationId === conversationId ? { ...c, unreadCount: 0 } : c
        ),
        messages: {
          ...state.messages,
          [conversationId]: (state.messages[conversationId] || []).map(m => ({
            ...m,
            isRead: true
          }))
        }
      }));
      get().refreshUnreadCount();
    } catch {
      // Non-critical — don't surface error
    }
  },

  setActiveConversation: (conversationId: string | null) => {
    set({ activeConversationId: conversationId });
  },

  refreshUnreadCount: async () => {
    try {
      const count = await api.messages.getUnreadCount();
      set({ unreadCount: count });
    } catch {
      // Ignore polling errors
    }
  }
}));
